import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import {
  useAdForgeGetBrandKitQuery,
  useAdForgeUpdateBrandKitMutation,
  useAdForgeUploadBrandFontMutation,
  type BrandKitColors,
  type BrandKitCustomFont,
} from "~/ui-rtk/api/adForgeApi"
import { useGetAssetQuery } from "~/ui-rtk/api/assetsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import ComboboxInput from "~/ui-rtk/components/ui/controls/ComboboxInput"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useAssetUpload from "../useAssetUpload"
import { FONT_CATALOG } from "./font-catalog"

const FONT_OPTIONS = FONT_CATALOG.map((family, index) => ({
  id: index + 1,
  label: family,
  value: family,
}))

/**
 * Brand Kit editor — the company design system every AI overlay-canvas call
 * is constrained by: palette, logo, typography, and the yes/no rules.
 * Persists at `company.metadata.settings.brandKit` (same metadata-settings
 * pattern as the AdForge defaults in Settings → Ad Blaster).
 */

const HEX_RE = /^#[0-9a-fA-F]{6}$/

const COLOR_ROLES: Array<{ key: keyof BrandKitColors; label: string }> = [
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "accent", label: "Accent" },
  { key: "background", label: "Background" },
  { key: "text", label: "Text" },
]

interface BrandKitModalProps {
  open: boolean
  onClose: () => void
}

export default function BrandKitModal({ open, onClose }: BrandKitModalProps) {
  const { data, isFetching } = useAdForgeGetBrandKitQuery(undefined, {
    skip: !open,
  })
  const [updateBrandKit, { isLoading: isSaving }] =
    useAdForgeUpdateBrandKitMutation()
  const [uploadBrandFont, { isLoading: isUploadingFont }] =
    useAdForgeUploadBrandFontMutation()
  const { uploadFiles, isUploading } = useAssetUpload()

  const [colors, setColors] = useState<Record<string, string>>({})
  const [extras, setExtras] = useState<string[]>([])
  const [logoAssetId, setLogoAssetId] = useState<string>("")
  // Set right after an upload so the preview doesn't wait on the asset query.
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("")
  const [headingFont, setHeadingFont] = useState("")
  const [bodyFont, setBodyFont] = useState("")
  const [customFonts, setCustomFonts] = useState<BrandKitCustomFont[]>([])
  const fontFileInputRef = useRef<HTMLInputElement>(null)
  const [dosText, setDosText] = useState("")
  const [dontsText, setDontsText] = useState("")
  const [tone, setTone] = useState("")
  const logoInputRef = useRef<HTMLInputElement>(null)

  const { data: logoAsset } = useGetAssetQuery(logoAssetId, {
    skip: !open || !logoAssetId || !!logoPreviewUrl,
  })
  const logoUrl = logoPreviewUrl || logoAsset?.thumbnailUrl || logoAsset?.url

  useEffect(() => {
    if (!open) return
    const nextColors: Record<string, string> = {}
    for (const role of COLOR_ROLES) {
      const value = data?.colors?.[role.key]
      if (typeof value === "string") nextColors[role.key] = value
    }
    setColors(nextColors)
    setExtras(data?.colors?.extras ?? [])
    setLogoAssetId(data?.logoAssetId ?? "")
    setLogoPreviewUrl("")
    setHeadingFont(data?.fonts?.heading ?? "")
    setBodyFont(data?.fonts?.body ?? "")
    setCustomFonts(data?.customFonts ?? [])
    setDosText((data?.dos ?? []).join("\n"))
    setDontsText((data?.donts ?? []).join("\n"))
    setTone(data?.tone ?? "")
  }, [open, data])

  const handleLogoFile = async (file: File | undefined): Promise<void> => {
    if (!file) return
    const [uploaded] = await uploadFiles([file], {
      metadata: { purpose: "brand_kit_logo" },
    })
    if (!uploaded?.assetId) {
      toast.error("Logo upload failed — try again")
      return
    }
    setLogoAssetId(uploaded.assetId)
    setLogoPreviewUrl(uploaded.thumbnailUrl || uploaded.url)
  }

  // Uploaded families join the pickers ahead of the Google Fonts catalog.
  const fontOptions = useMemo(
    () => [
      ...customFonts.map((font, index) => ({
        id: 10_000 + index,
        label: `${font.family} (uploaded)`,
        value: font.family,
      })),
      ...FONT_OPTIONS,
    ],
    [customFonts],
  )

  const handleFontFile = async (file: File | undefined): Promise<void> => {
    if (!file) return
    try {
      // Dedicated endpoint — fonts go straight to S3, never into the asset
      // library.
      const { url } = await uploadBrandFont(file).unwrap()
      // Family name from the filename:
      // "BrandGrotesk-Bold.woff2" → "BrandGrotesk Bold".
      const family = file.name
        .replace(/\.(woff2?|ttf|otf)$/i, "")
        .replace(/[-_]+/g, " ")
        .trim()
      setCustomFonts(prev => [
        ...prev,
        { family: family || "Custom font", url },
      ])
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message
      toast.error(message ?? "Font upload failed — try again")
    }
  }

  const splitLines = (text: string): string[] =>
    text
      .split("\n")
      .map(line => line.trim())
      .filter(Boolean)

  const handleSave = async (): Promise<void> => {
    const invalid = [...Object.values(colors), ...extras].filter(
      value => value && !HEX_RE.test(value),
    )
    if (invalid.length) {
      toast.error(
        `Invalid color${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`,
      )
      return
    }
    try {
      const colorPayload: BrandKitColors = {}
      for (const role of COLOR_ROLES) {
        const value = colors[role.key]
        if (value) colorPayload[role.key] = value as never
      }
      if (extras.length) colorPayload.extras = extras
      await updateBrandKit({
        colors: colorPayload,
        logoAssetId: logoAssetId || undefined,
        fonts: {
          ...(headingFont.trim() ? { heading: headingFont.trim() } : {}),
          ...(bodyFont.trim() ? { body: bodyFont.trim() } : {}),
        },
        customFonts,
        dos: splitLines(dosText),
        donts: splitLines(dontsText),
        tone: tone.trim(),
      }).unwrap()
      toast.success("Brand kit saved")
      onClose()
    } catch (err) {
      const message = (err as { data?: { message?: string | string[] } })?.data
        ?.message
      toast.error(
        Array.isArray(message)
          ? message[0]
          : (message ?? "Failed to save brand kit"),
      )
    }
  }

  return (
    <Dialog
      isOpen={open}
      handleClose={onClose}
      width={720}
      closeOnClickOutside
      classes={{
        container:
          "rounded-lg bg-system-blue-800 text-left text-white shadow-xl",
      }}
    >
      <div className="flex items-center justify-between border-b border-system-blue-700 px-system-lg py-system-md">
        <div>
          <h2 className="text-lg font-semibold">Brand Kit</h2>
          <p className="text-system-gray-300 text-xs">
            The design system the AI overlay engine works within — colors, logo,
            fonts, and your do&apos;s &amp; don&apos;ts.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-system-gray-300 hover:text-white"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex max-h-[70vh] flex-col gap-system-lg overflow-y-auto px-system-lg py-system-md">
        {isFetching && !data ? (
          <div className="flex items-center justify-center gap-system-2xs py-system-lg text-center text-system-gray-300">
            <span className="loading loading-spinner loading-sm" />
            Loading brand kit…
          </div>
        ) : (
          <>
            {/* Colors */}
            <section className="flex flex-col gap-system-2xs">
              <h3 className="text-sm font-medium text-white">Colors</h3>
              <p className="text-system-gray-300 text-xs">
                Overlays only use these colors. Leave a role empty if it
                doesn&apos;t apply.
              </p>
              <div className="grid grid-cols-2 gap-system-sm sm:grid-cols-3">
                {COLOR_ROLES.map(role => {
                  const value = colors[role.key] ?? ""
                  return (
                    <div key={role.key} className="flex items-center gap-2">
                      <input
                        type="color"
                        value={HEX_RE.test(value) ? value : "#000000"}
                        onChange={e =>
                          setColors(prev => ({
                            ...prev,
                            [role.key]: e.target.value.toUpperCase(),
                          }))
                        }
                        className="h-8 w-8 cursor-pointer rounded border border-system-blue-600 bg-transparent p-0"
                        title={role.label}
                      />
                      <div className="flex flex-col">
                        <span className="text-[11px] text-system-gray-300">
                          {role.label}
                        </span>
                        <input
                          value={value}
                          onChange={e =>
                            setColors(prev => ({
                              ...prev,
                              [role.key]: e.target.value.trim(),
                            }))
                          }
                          placeholder="#RRGGBB"
                          className={cn(
                            "w-24 bg-transparent font-mono text-xs text-white outline-none border-b",
                            value && !HEX_RE.test(value)
                              ? "border-system-red-400"
                              : "border-system-blue-600",
                          )}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>

            {/* Logo */}
            <section className="flex flex-col gap-system-2xs">
              <h3 className="text-sm font-medium text-white">Logo</h3>
              <p className="text-system-gray-300 text-xs">
                A transparent PNG works best — it&apos;s placed as its own layer
                on overlays.
              </p>
              <div className="flex items-center gap-system-sm">
                <div className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed border-system-blue-600 bg-system-blue-900/60 overflow-hidden">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Brand logo"
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-[10px] text-system-gray-400">
                      No logo
                    </span>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={e => void handleLogoFile(e.target.files?.[0])}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploading}
                  className="rounded-md bg-system-blue-700 px-system-md py-system-2xs text-sm text-white hover:bg-system-blue-600 disabled:opacity-50"
                >
                  {isUploading
                    ? "Uploading…"
                    : logoUrl
                      ? "Replace logo"
                      : "Upload logo"}
                </button>
              </div>
            </section>

            {/* Fonts */}
            <section className="flex flex-col gap-system-2xs">
              <h3 className="text-sm font-medium text-white">Fonts</h3>
              <p className="text-system-gray-300 text-xs">
                Family names (e.g. &quot;Montserrat&quot;). Google Fonts are
                loaded automatically for previews and exports.
              </p>
              <div className="grid grid-cols-2 gap-system-sm">
                <ComboboxInput
                  value={headingFont}
                  onChange={setHeadingFont}
                  options={fontOptions}
                  placeholder="Heading font — type to search"
                />
                <ComboboxInput
                  value={bodyFont}
                  onChange={setBodyFont}
                  options={fontOptions}
                  placeholder="Body font — type to search"
                />
              </div>

              {/* Company-owned fonts */}
              <div className="mt-system-sm flex flex-col gap-system-2xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-white">
                    Your fonts
                  </span>
                  <input
                    ref={fontFileInputRef}
                    type="file"
                    accept=".woff2,.woff,.ttf,.otf"
                    className="hidden"
                    onChange={e => {
                      void handleFontFile(e.target.files?.[0])
                      e.target.value = ""
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fontFileInputRef.current?.click()}
                    disabled={isUploadingFont || customFonts.length >= 8}
                    className="rounded-md bg-system-blue-700 px-system-md py-system-2xs text-sm text-white hover:bg-system-blue-600 disabled:opacity-50"
                  >
                    {isUploadingFont ? "Uploading…" : "Upload font file"}
                  </button>
                </div>
                <p className="text-system-gray-300 text-xs">
                  woff2 / woff / ttf / otf. Uploaded families appear in the
                  pickers above and render in previews and exports.
                </p>
                {customFonts.length > 0 && (
                  <div className="flex flex-col gap-system-2xs">
                    {customFonts.map((font, index) => (
                      <div
                        key={font.url}
                        className="flex items-center gap-system-sm rounded-md border border-system-blue-700 px-system-sm py-system-2xs"
                      >
                        <input
                          value={font.family}
                          onChange={e =>
                            setCustomFonts(prev =>
                              prev.map((f, i) =>
                                i === index
                                  ? { ...f, family: e.target.value }
                                  : f,
                              ),
                            )
                          }
                          className="flex-1 bg-transparent text-sm text-white outline-none border-b border-system-blue-600"
                          placeholder="Family name"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setCustomFonts(prev =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                          className="text-system-gray-300 hover:text-system-red-400"
                          aria-label={`Remove ${font.family}`}
                          title="Remove font"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Rules */}
            <section className="grid grid-cols-1 gap-system-sm sm:grid-cols-2">
              <div className="flex flex-col gap-system-2xs">
                <h3 className="text-sm font-medium text-white">Do&apos;s</h3>
                <p className="text-system-gray-300 text-xs">
                  One rule per line — strong preferences for every overlay.
                </p>
                <textarea
                  value={dosText}
                  onChange={e => setDosText(e.target.value)}
                  rows={5}
                  placeholder={"Keep copy under 6 words\nLogo bottom-right"}
                  className="textarea textarea-sm w-full bg-system-blue-900/60 border-system-blue-600 text-white"
                />
              </div>
              <div className="flex flex-col gap-system-2xs">
                <h3 className="text-sm font-medium text-white">Don&apos;ts</h3>
                <p className="text-system-gray-300 text-xs">
                  One rule per line — hard constraints the AI must never break.
                </p>
                <textarea
                  value={dontsText}
                  onChange={e => setDontsText(e.target.value)}
                  rows={5}
                  placeholder={"Never cover faces\nNo drop shadows"}
                  className="textarea textarea-sm w-full bg-system-blue-900/60 border-system-blue-600 text-white"
                />
              </div>
            </section>

            {/* Tone */}
            <section className="flex flex-col gap-system-2xs">
              <h3 className="text-sm font-medium text-white">Tone of voice</h3>
              <textarea
                value={tone}
                onChange={e => setTone(e.target.value)}
                rows={2}
                placeholder="Confident, playful, never salesy…"
                className="textarea textarea-sm w-full bg-system-blue-900/60 border-system-blue-600 text-white"
              />
            </section>
          </>
        )}
      </div>

      <div className="flex items-center justify-end gap-system-sm border-t border-system-blue-700 px-system-lg py-system-md">
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-system-gray-200 hover:text-white"
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || isUploading}
          className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </Dialog>
  )
}
